import {
  Suspense,
  lazy,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@heroui/button";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import clsx from "clsx";

import { searchCities, searchSubjects } from "@/api/metadata";

// Loaded on first mobile open only — keeps the heavy @heroui/modal out of the
// eager entry chunk (desktop never fetches it).
const MobileSuggestionsSheet = lazy(() => import("./TutorSearchMobileSheet"));

export type TutorSearchCriteria = {
  subject: string | null;
  city: string | null;
  keyword: string;
};

type TutorSearchBarProps = {
  initialSubject?: string | null;
  initialCity?: string | null;
  initialKeyword?: string;
  popularSubjects?: string[];
  popularCities?: string[];
  onSubmit: (criteria: TutorSearchCriteria) => void;
  /**
   * Fires on every change inside the bar (segment pick, clear, keyword
   * keystroke). Use it to keep a parent-controlled state — e.g. a live
   * filter on the home page — in sync without forcing a click on the
   * submit button.
   */
  onChange?: (criteria: TutorSearchCriteria) => void;
  variant?: "hero" | "page-top";
  isLoading?: boolean;
};

type SegmentKey = "subject" | "city" | "keyword";

const ROTATING_PLACEHOLDERS = [
  "Préparation Bac",
  "Concours prépa",
  "Cours en visio",
  "Pédagogue",
  "Terminale Maths",
  "Aide aux devoirs",
];

const DEBOUNCE_MS = 220;

// Shared surface tint applied over the theme-aware content1 token. Used by
// both the search bar and its suggestions popover so they stay in sync.
const SURFACE_GRADIENT_BG =
  "linear-gradient(180deg, rgba(241, 243, 248, 0.05) 0%, rgba(241, 243, 248, 0.02) 100%), hsl(var(--heroui-content1))";

/* ── Helpers ─────────────────────────────────────────────────────── */

function normalize(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function dedupe(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const item of items) {
    const key = normalize(item);

    if (key && !seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }

  return out;
}

/* ── Component ───────────────────────────────────────────────────── */

export function TutorSearchBar({
  initialSubject = null,
  initialCity = null,
  initialKeyword = "",
  popularSubjects = [],
  popularCities = [],
  onSubmit,
  onChange,
  variant = "hero",
  isLoading = false,
}: TutorSearchBarProps) {
  const prefersReducedMotion = useReducedMotion();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverInputRef = useRef<HTMLInputElement | null>(null);
  const idBase = useId();

  const [subject, setSubject] = useState<string | null>(initialSubject);
  const [city, setCity] = useState<string | null>(initialCity);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [openSegment, setOpenSegment] = useState<SegmentKey | null>(null);
  const [popoverQuery, setPopoverQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // ── Fire onChange whenever criteria mutate ──────────────────
  // Skip the very first render so the parent doesn't get an
  // "echo" of its own initial values immediately.
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;

      return;
    }
    onChange?.({ subject, city, keyword: keyword.trim() });
  }, [subject, city, keyword]);

  // ── Rotating placeholder for keyword segment ─────────────────
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      setPlaceholderIndex(
        (index) => (index + 1) % ROTATING_PLACEHOLDERS.length,
      );
    }, 3000);

    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  // ── Debounced filter for popover query ───────────────────────
  useEffect(() => {
    if (!openSegment || openSegment === "keyword") {
      setFilteredResults([]);

      return;
    }
    if (!popoverQuery.trim()) {
      setFilteredResults([]);

      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const fetcher =
          openSegment === "subject" ? searchSubjects : searchCities;
        const items = await fetcher(popoverQuery.trim(), 30);

        if (!cancelled) setFilteredResults(dedupe(items));
      } catch {
        if (!cancelled) setFilteredResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [popoverQuery, openSegment]);

  // ── Outside-click + Esc to close ─────────────────────────────
  // Listen on "click" (not "mousedown") so the chip's React onClick has
  // already executed by the time we evaluate. The popover lives inside
  // wrapperRef on desktop; the mobile sheet's outside-click is handled
  // natively by HeroUI Modal (which is only mounted on mobile).
  useEffect(() => {
    if (!openSegment) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) return;
      if (wrapperRef.current && wrapperRef.current.contains(target)) return;
      closeSegment();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSegment();
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [openSegment]);

  // ── Auto-focus the popover input when it opens ───────────────
  useEffect(() => {
    if (openSegment && openSegment !== "keyword") {
      // Defer so the popover is in the DOM.
      const id = window.setTimeout(() => {
        popoverInputRef.current?.focus();
      }, 30);

      return () => clearTimeout(id);
    }
  }, [openSegment]);

  // ── Derived: items to display in popover ─────────────────────
  const popoverItems = useMemo(() => {
    if (!openSegment || openSegment === "keyword") return [];
    const source = openSegment === "subject" ? popularSubjects : popularCities;

    if (popoverQuery.trim()) return filteredResults;

    return dedupe(source);
  }, [
    openSegment,
    popoverQuery,
    popularSubjects,
    popularCities,
    filteredResults,
  ]);

  function openSegmentNow(next: SegmentKey) {
    setOpenSegment(next);
    setPopoverQuery("");
    setFocusedIndex(-1);
  }

  function closeSegment() {
    setOpenSegment(null);
    setPopoverQuery("");
    setFocusedIndex(-1);
  }

  // Brief saffron pulse on the bar when a value is just picked — gives
  // immediate visual feedback that the segment took the new value.
  const [recentlyPicked, setRecentlyPicked] = useState<SegmentKey | null>(null);

  // Track viewport so we only mount the mobile Modal on mobile. HeroUI's
  // Modal installs an outside-click trap whenever it's mounted with
  // `isOpen=true`, even if hidden via `md:hidden` CSS — that trap was
  // eating chip clicks in the desktop popover.
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileViewport(mql.matches);

    update();
    mql.addEventListener("change", update);

    return () => mql.removeEventListener("change", update);
  }, []);

  // The mobile sheet (and its heavy modal chunk) is only fetched the first time
  // a mobile user actually opens a subject/city segment. Once requested it stays
  // mounted so subsequent open/close transitions still animate.
  const [mobileSheetRequested, setMobileSheetRequested] = useState(false);

  useEffect(() => {
    if (isMobileViewport && openSegment && openSegment !== "keyword") {
      setMobileSheetRequested(true);
    }
  }, [isMobileViewport, openSegment]);

  // Segment is passed explicitly (not read from closure) so this can't
  // get out of sync with rapid open/close cycles.
  function chooseValue(segment: SegmentKey, value: string | null) {
    if (segment === "subject") setSubject(value);
    if (segment === "city") setCity(value);
    closeSegment();
    if (value) {
      setRecentlyPicked(segment);
      window.setTimeout(() => setRecentlyPicked(null), 600);
    }
  }

  function handlePopoverKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex((index) => Math.min(index + 1, popoverItems.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex((index) => Math.max(index - 1, -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!openSegment || openSegment === "keyword") return;
      if (focusedIndex >= 0 && popoverItems[focusedIndex]) {
        chooseValue(openSegment, popoverItems[focusedIndex]);
      } else if (popoverQuery.trim()) {
        chooseValue(openSegment, popoverQuery.trim());
      }
    }
  }

  function handleSubmit() {
    onSubmit({ subject, city, keyword: keyword.trim() });
  }

  function handleKeywordKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  }

  const heroSizing = variant === "hero";

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* ── Instruction line above the bar ─────────────────── */}
      <div className="mb-2 flex items-center gap-2 px-1">
        <span aria-hidden className="text-[11px]">
          👇
        </span>
        <p className="text-[11.5px] font-medium text-default-500">
          Cliquez sur un champ pour choisir une matière ou une ville, puis{" "}
          <span className="text-primary">Rechercher</span>.
        </p>
      </div>

      {/* ── Main bar ─────────────────────────────────────────── */}
      <m.div
        animate={{
          boxShadow: recentlyPicked
            ? "0 0 0 3px rgba(250,181,25,0.35), 0 30px 70px -30px rgba(250,181,25,0.4)"
            : heroSizing
              ? "0 30px 70px -30px rgba(250,181,25,0.25)"
              : "0 0 0 0 rgba(0,0,0,0)",
        }}
        className={clsx(
          "editorial-surface relative z-20 grid w-full overflow-visible rounded-2xl",
          "grid-cols-1 divide-y divide-default-200/30 md:grid-cols-[1.2fr_1.2fr_1.4fr_auto] md:divide-x md:divide-y-0",
        )}
        style={{ background: SURFACE_GRADIENT_BG }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        {/* Saffron focus glow ring (animated) */}
        {openSegment && (
          <m.span
            aria-hidden
            animate={{ opacity: 1 }}
            className="pointer-events-none absolute -inset-0.5 -z-10 rounded-2xl"
            initial={{ opacity: 0 }}
            style={{
              background:
                "linear-gradient(135deg, rgba(250,181,25,0.35), rgba(250,181,25,0))",
              filter: "blur(12px)",
            }}
            transition={{ duration: 0.25 }}
          />
        )}

        <SegmentButton
          controlsId={`${idBase}-subject-popover`}
          icon="📚"
          isActive={openSegment === "subject"}
          label="Matière"
          placeholder="Toutes les matières"
          value={subject}
          variant={variant}
          onClear={() => setSubject(null)}
          onOpen={() => openSegmentNow("subject")}
        />
        <SegmentButton
          controlsId={`${idBase}-city-popover`}
          icon="📍"
          isActive={openSegment === "city"}
          label="Ville"
          placeholder="Toutes les villes"
          value={city}
          variant={variant}
          onClear={() => setCity(null)}
          onOpen={() => openSegmentNow("city")}
        />

        {/* Keyword segment: icon-disc + label + free-text input */}
        <label
          className={clsx(
            "group relative flex w-full cursor-text items-center gap-3 transition-colors",
            "px-4 md:px-5",
            heroSizing ? "py-4" : "py-3.5",
            openSegment === "keyword"
              ? "bg-primary/10"
              : "hover:bg-content2/50",
          )}
          htmlFor={`${idBase}-keyword`}
        >
          <span
            aria-hidden
            className={clsx(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg transition-colors",
              openSegment === "keyword"
                ? "bg-primary/20 ring-1 ring-primary/30"
                : "bg-content2/60 group-hover:bg-primary/10",
            )}
          >
            ✨
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="editorial-overline text-default-500">
              Mots-clés
            </span>
            <span className="relative mt-0.5">
              <input
                aria-label="Mots-clés de recherche"
                autoComplete="off"
                className="w-full bg-transparent text-[15px] font-semibold leading-tight text-foreground outline-none placeholder:text-default-400"
                id={`${idBase}-keyword`}
                placeholder=" "
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                onFocus={() => setOpenSegment("keyword")}
                onKeyDown={handleKeywordKeyDown}
              />
              {/* Rotating placeholder when input is empty */}
              {!keyword && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-[15px] font-medium text-default-500"
                >
                  Ex.{" "}
                  <AnimatePresence initial={false} mode="wait">
                    <m.span
                      key={ROTATING_PLACEHOLDERS[placeholderIndex]}
                      animate={{ opacity: 1, y: 0 }}
                      className="inline-block"
                      exit={{ opacity: 0, y: -6 }}
                      initial={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.35 }}
                    >
                      {ROTATING_PLACEHOLDERS[placeholderIndex]}
                    </m.span>
                  </AnimatePresence>
                </span>
              )}
            </span>
          </span>
          {/* Saffron underline when active */}
          {openSegment === "keyword" && (
            <m.span
              aria-hidden
              animate={{ scaleX: 1 }}
              className="absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-primary"
              initial={{ scaleX: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </label>

        {/* Submit */}
        <div
          className={clsx(
            "flex items-center justify-center md:px-2",
            heroSizing ? "py-3 md:py-2.5" : "py-2.5 md:py-2",
          )}
        >
          <Button
            aria-label="Rechercher"
            className={clsx(
              "w-full font-semibold shadow-lg shadow-primary/30 md:w-auto",
              heroSizing ? "h-[52px] px-6 text-[14px]" : "h-[44px] px-5",
            )}
            color="primary"
            isLoading={isLoading}
            radius="full"
            onPress={handleSubmit}
          >
            <span className="flex items-center gap-2">
              <SearchIcon />
              Rechercher
            </span>
          </Button>
        </div>
      </m.div>

      {/* ── Desktop popover (subject / city) ─────────────────── */}
      {openSegment && openSegment !== "keyword" && (
        <div className="hidden md:block">
          <SuggestionsPopover
            activeQuery={popoverQuery}
            currentValue={openSegment === "subject" ? subject : city}
            focusedIndex={focusedIndex}
            inputRef={popoverInputRef}
            isLoading={isSearching}
            items={popoverItems}
            popoverId={`${idBase}-${openSegment}-popover`}
            popularEmpty={popoverQuery.trim().length === 0}
            segmentLabel={openSegment === "subject" ? "Matière" : "Ville"}
            onClear={() => chooseValue(openSegment, null)}
            onClose={closeSegment}
            onFocusedIndexChange={setFocusedIndex}
            onKeyDown={handlePopoverKeyDown}
            onPick={(value) => chooseValue(openSegment, value)}
            onQueryChange={setPopoverQuery}
          />
        </div>
      )}

      {/* ── Mobile bottom-sheet (subject / city) — lazy ─────── */}
      {mobileSheetRequested && (
        <Suspense fallback={null}>
          <MobileSuggestionsSheet
            activeQuery={popoverQuery}
            currentValue={
              openSegment === "subject"
                ? subject
                : openSegment === "city"
                  ? city
                  : null
            }
            focusedIndex={focusedIndex}
            isLoading={isSearching}
            isOpen={
              isMobileViewport &&
              Boolean(openSegment) &&
              openSegment !== "keyword"
            }
            items={popoverItems}
            popularEmpty={popoverQuery.trim().length === 0}
            segmentLabel={openSegment === "subject" ? "Matière" : "Ville"}
            onClear={() => {
              if (openSegment === "subject" || openSegment === "city") {
                chooseValue(openSegment, null);
              }
            }}
            onClose={closeSegment}
            onFocusedIndexChange={setFocusedIndex}
            onPick={(value) => {
              if (openSegment === "subject" || openSegment === "city") {
                chooseValue(openSegment, value);
              }
            }}
            onQueryChange={setPopoverQuery}
          />
        </Suspense>
      )}
    </div>
  );
}

/* ── Sub-component: each clickable segment ───────────────────────── */

function SegmentButton({
  label,
  icon,
  placeholder,
  value,
  isActive,
  variant,
  controlsId,
  onOpen,
  onClear,
}: {
  label: string;
  icon: string;
  placeholder: string;
  value: string | null;
  isActive: boolean;
  variant: "hero" | "page-top";
  controlsId: string;
  onOpen: () => void;
  onClear: () => void;
}) {
  const heroSizing = variant === "hero";

  return (
    <button
      aria-controls={controlsId}
      aria-expanded={isActive}
      aria-haspopup="listbox"
      aria-label={`${label}: ${value ?? placeholder}. Cliquez pour ouvrir les suggestions.`}
      className={clsx(
        "group relative flex w-full items-center gap-3 text-left transition-colors",
        "px-4 md:px-5",
        heroSizing ? "py-4" : "py-3.5",
        isActive ? "bg-primary/10" : "hover:bg-content2/50",
        "focus-visible:bg-content2/60 focus-visible:outline-none",
      )}
      type="button"
      onClick={onOpen}
    >
      {/* Icon on the left */}
      <span
        aria-hidden
        className={clsx(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg transition-colors",
          isActive
            ? "bg-primary/20 ring-1 ring-primary/30"
            : "bg-content2/60 group-hover:bg-primary/10",
        )}
      >
        {icon}
      </span>

      {/* Label + value */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="editorial-overline text-default-500">{label}</span>
        <span className="mt-0.5 flex items-center gap-2">
          <span
            className={clsx(
              "truncate text-[15px] font-semibold leading-tight",
              value ? "text-foreground" : "text-default-500",
            )}
          >
            {value ?? placeholder}
          </span>
          {value && (
            <span
              aria-hidden
              className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9.5px] font-semibold text-primary"
            >
              ✓
            </span>
          )}
        </span>
      </span>

      {/* Right side: clear-X if value present, otherwise chevron-down hint */}
      {value ? (
        <span
          aria-label={`Effacer ${label.toLowerCase()}`}
          className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-default-400 transition-colors hover:bg-danger-500/15 hover:text-danger-500"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onClear();
            }
          }}
        >
          <svg
            aria-hidden="true"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </span>
      ) : (
        <m.span
          aria-hidden
          animate={{ y: isActive ? 0 : [0, 2, 0] }}
          className={clsx(
            "flex shrink-0 items-center text-default-400 transition-colors",
            isActive ? "text-primary" : "group-hover:text-primary",
          )}
          transition={{
            duration: 1.6,
            repeat: isActive ? 0 : Infinity,
            ease: "easeInOut",
          }}
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </m.span>
      )}

      {/* Saffron underline when active */}
      {isActive && (
        <m.span
          aria-hidden
          animate={{ scaleX: 1 }}
          className="absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-primary"
          initial={{ scaleX: 0 }}
          transition={{ duration: 0.25 }}
        />
      )}
    </button>
  );
}

/* ── Sub-component: popover dropdown ─────────────────────────────── */

function SuggestionsPopover({
  segmentLabel,
  activeQuery,
  onQueryChange,
  items,
  popularEmpty,
  focusedIndex,
  onFocusedIndexChange,
  onPick,
  onClose,
  onKeyDown,
  inputRef,
  isLoading,
  currentValue,
  onClear,
  popoverId,
}: {
  segmentLabel: string;
  activeQuery: string;
  onQueryChange: (query: string) => void;
  items: string[];
  popularEmpty: boolean;
  focusedIndex: number;
  onFocusedIndexChange: (index: number) => void;
  onPick: (value: string) => void;
  onClose: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  isLoading: boolean;
  currentValue: string | null;
  onClear: () => void;
  popoverId: string;
}) {
  return (
    <m.div
      animate={{ opacity: 1, y: 0 }}
      aria-label={`Suggestions ${segmentLabel}`}
      className="editorial-surface absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl"
      data-tutorsearch-popover="desktop"
      id={popoverId}
      initial={{ opacity: 0, y: -8 }}
      role="dialog"
      style={{ background: SURFACE_GRADIENT_BG }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      {/* Search input header */}
      <div className="flex items-center gap-3 border-b border-default-200/30 px-4 py-3">
        <span aria-hidden className="text-default-400">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          autoComplete="off"
          className="flex-1 bg-transparent text-[14.5px] text-foreground outline-none placeholder:text-default-400"
          placeholder={`Tapez pour filtrer · ${segmentLabel.toLowerCase()}`}
          type="text"
          value={activeQuery}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {currentValue && (
          <button
            className="text-[11.5px] text-default-500 hover:text-foreground"
            type="button"
            onClick={onClear}
          >
            Effacer
          </button>
        )}
        <button
          aria-label="Fermer"
          className="rounded-full p-1 text-default-400 transition-colors hover:bg-default-200/30 hover:text-foreground"
          type="button"
          onClick={onClose}
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[360px] overflow-y-auto">
        {/* Popular chips (only when no query) */}
        {popularEmpty && items.length > 0 && (
          <div className="px-4 pb-2 pt-3">
            <p className="editorial-overline mb-2 text-default-500">
              {segmentLabel === "Matière"
                ? "Matières populaires"
                : "Villes principales"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {items.slice(0, 12).map((item) => (
                <button
                  key={item}
                  className={clsx(
                    "rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                    currentValue === item
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-default-200/50 bg-background/40 text-foreground/85 hover:border-primary/40 hover:bg-primary/5",
                  )}
                  type="button"
                  onClick={() => onPick(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search results list (when query) */}
        {!popularEmpty && (
          <div className="py-2">
            {isLoading && items.length === 0 && (
              <div className="px-4 py-6 text-center text-[12.5px] text-default-500">
                Recherche en cours…
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="px-4 py-6 text-center text-[12.5px] text-default-500">
                Aucun résultat, appuyez sur{" "}
                <kbd className="rounded bg-default-200/40 px-1.5 py-0.5 text-[10px]">
                  Entrée
                </kbd>{" "}
                pour utiliser « {activeQuery} » tel quel.
              </div>
            )}
            {items.map((item, index) => {
              const isFocused = index === focusedIndex;
              const isSelected = currentValue === item;

              return (
                <button
                  key={item}
                  aria-selected={isSelected}
                  className={clsx(
                    "flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-[13.5px] transition-colors",
                    isFocused && "bg-primary/10",
                    !isFocused && "hover:bg-content1/50 focus:bg-content1/50",
                  )}
                  role="option"
                  type="button"
                  onClick={() => onPick(item)}
                  onMouseEnter={() => onFocusedIndexChange(index)}
                >
                  <span
                    className={
                      isSelected
                        ? "font-semibold text-primary"
                        : "text-foreground/90"
                    }
                  >
                    {item}
                  </span>
                  {isSelected && (
                    <span
                      aria-hidden
                      className="text-[11px] font-semibold text-primary"
                    >
                      ✓ Sélectionné
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="border-t border-default-200/30 px-4 py-2 text-[10.5px] text-default-400">
        <kbd className="rounded bg-default-200/40 px-1.5 py-0.5">↑</kbd>{" "}
        <kbd className="rounded bg-default-200/40 px-1.5 py-0.5">↓</kbd> pour
        naviguer ·{" "}
        <kbd className="rounded bg-default-200/40 px-1.5 py-0.5">Entrée</kbd>{" "}
        pour sélectionner ·{" "}
        <kbd className="rounded bg-default-200/40 px-1.5 py-0.5">Esc</kbd> pour
        fermer
      </div>
    </m.div>
  );
}

/* ── Icon ────────────────────────────────────────────────────────── */

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}
